import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { Cell, Label, Pie, PieChart, Tooltip } from 'recharts';

import type { AggregatedStat, TAttributionModel } from '@feathr/blackbox';
import { useStore } from '@feathr/report_components/state';

import {
  breakdownTooltipRenderer,
  buildBreakdownData,
  getCenterX,
  getInnerRadius,
  getOuterRadius,
} from './ConversionBreakdownChart.utils';

import * as styles from './ConversionBreakdownChart.css';

interface IConversionBreakdownChartProps {
  aggregatedStats: AggregatedStat;
  attributionModel: TAttributionModel;
}

function ConversionBreakdownChartComponent({
  aggregatedStats,
  attributionModel,
}: Readonly<IConversionBreakdownChartProps>): JSX.Element | null {
  const reportStore = useStore();

  const {
    segmentPieDataFormatted,
    goalPieDataFormatted,
    categoryPieDataFormatted,
    showSegsChart,
    showGoalsChart,
    showCatsChart,
    showConversionBreakdown,
    showROIBreakdown,
    showBreakdowns,
    breakdownSize,
    segmentColors,
    goalColors,
    catColors,
    conversionsLabelContent,
    roiLabelContent,
  } = buildBreakdownData(aggregatedStats, attributionModel);

  if (!showBreakdowns) {
    return null;
  }

  return (
    <div className={styles.chartsBox}>
      <Label>Breakdown</Label>
      <PieChart height={380} width={750}>
        {showConversionBreakdown && showSegsChart ? (
          <Pie
            cx={getCenterX(0, breakdownSize)}
            cy={180}
            data={segmentPieDataFormatted}
            dataKey={'num'}
            innerRadius={getInnerRadius(
              2,
              breakdownSize,
              showCatsChart,
              showGoalsChart,
              showSegsChart,
            )}
            outerRadius={getOuterRadius(
              2,
              breakdownSize,
              showCatsChart,
              showGoalsChart,
              showSegsChart,
            )}
          >
            <Label content={conversionsLabelContent} position={'center'} value={'Conversions'} />
            {segmentColors.map((item, index) => (
              <Cell fill={item.color} key={`cell-${index}`} />
            ))}
          </Pie>
        ) : (
          <></>
        )}
        {showConversionBreakdown && showGoalsChart ? (
          <Pie
            cx={getCenterX(0, breakdownSize)}
            cy={180}
            data={goalPieDataFormatted}
            dataKey={'num'}
            innerRadius={getInnerRadius(
              1,
              breakdownSize,
              showCatsChart,
              showGoalsChart,
              showSegsChart,
            )}
            outerRadius={getOuterRadius(
              1,
              breakdownSize,
              showCatsChart,
              showGoalsChart,
              showSegsChart,
            )}
          >
            <Label content={conversionsLabelContent} position={'center'} value={'Conversions'} />
            {goalColors.map((item, index) => (
              <Cell fill={item.color} key={`cell-${index}`} />
            ))}
          </Pie>
        ) : (
          <></>
        )}
        {showConversionBreakdown && showCatsChart ? (
          <Pie
            cx={getCenterX(0, breakdownSize)}
            cy={180}
            data={categoryPieDataFormatted}
            dataKey={'num'}
            innerRadius={getInnerRadius(
              0,
              breakdownSize,
              showCatsChart,
              showGoalsChart,
              showSegsChart,
            )}
            outerRadius={getOuterRadius(
              0,
              breakdownSize,
              showCatsChart,
              showGoalsChart,
              showSegsChart,
            )}
          >
            <Label content={conversionsLabelContent} position={'center'} value={'Conversions'} />
            {catColors.map((item, index) => (
              <Cell fill={item.color} key={`cell-${index}`} />
            ))}
          </Pie>
        ) : (
          <></>
        )}
        {showROIBreakdown && showSegsChart ? (
          <Pie
            cx={getCenterX(1, breakdownSize)}
            cy={180}
            data={segmentPieDataFormatted}
            dataKey={'roi'}
            innerRadius={getInnerRadius(
              2,
              breakdownSize,
              showCatsChart,
              showGoalsChart,
              showSegsChart,
            )}
            outerRadius={getOuterRadius(
              2,
              breakdownSize,
              showCatsChart,
              showGoalsChart,
              showSegsChart,
            )}
          >
            <Label content={roiLabelContent} position={'center'} value={'Conversion value'} />
            {segmentColors.map((item, index) => (
              <Cell fill={item.color} key={`cell-${index}`} />
            ))}
          </Pie>
        ) : (
          <></>
        )}
        {showROIBreakdown && showGoalsChart ? (
          <Pie
            cx={getCenterX(1, breakdownSize)}
            cy={180}
            data={goalPieDataFormatted}
            dataKey={'roi'}
            innerRadius={getInnerRadius(
              1,
              breakdownSize,
              showCatsChart,
              showGoalsChart,
              showSegsChart,
            )}
            outerRadius={getOuterRadius(
              1,
              breakdownSize,
              showCatsChart,
              showGoalsChart,
              showSegsChart,
            )}
          >
            <Label content={roiLabelContent} position={'center'} value={'Conversion value'} />
            {goalColors.map((item, index) => (
              <Cell fill={item.color} key={`cell-${index}`} />
            ))}
          </Pie>
        ) : (
          <></>
        )}
        {showROIBreakdown && showCatsChart ? (
          <Pie
            cx={getCenterX(1, breakdownSize)}
            cy={180}
            data={categoryPieDataFormatted}
            dataKey={'roi'}
            innerRadius={getInnerRadius(
              0,
              breakdownSize,
              showCatsChart,
              showGoalsChart,
              showSegsChart,
            )}
            outerRadius={getOuterRadius(
              0,
              breakdownSize,
              showCatsChart,
              showGoalsChart,
              showSegsChart,
            )}
          >
            <Label content={roiLabelContent} position={'center'} value={'Conversion value'} />
            {catColors.map((item, index) => (
              <Cell fill={item.color} key={`cell-${index}`} />
            ))}
          </Pie>
        ) : (
          <></>
        )}
        <Tooltip content={breakdownTooltipRenderer(reportStore)} />
      </PieChart>
    </div>
  );
}

const ConversionBreakdownChart = observer(ConversionBreakdownChartComponent);

export default ConversionBreakdownChart;
